import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface TenantState {
  id: string | null;
  subdomain: string | null;
  name?: string | null;
  logoUrl?: string | null;
}

function getInitialSubdomain(): string | null {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('subdomain');
    if (stored) return stored;
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('subdomain') || params.get('tenant');
    if (fromQuery) return fromQuery;
    const hostParts = window.location.hostname.split('.');
    if (hostParts.length > 3) return hostParts[0];
  }
  return null;
}

function getInitialTenantId(): string | null {
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage.getItem('tenantId');
      if (stored) return stored;
    } catch {}
  }
  return null;
}


const initialState: TenantState = {
  id: getInitialTenantId(),
  subdomain: getInitialSubdomain(),
  name: null,
  logoUrl: null,
};

type SetTenantPayload =
  | string
  | {
      id?: string | null;
      subdomain?: string | null;
      name?: string | null;
      logoUrl?: string | null;
    };


const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    // Backward compatible: allow string (treated as subdomain) or object payload
    setTenant(state, action: PayloadAction<SetTenantPayload>) {
      const p = action.payload as any;
      if (typeof p === 'string') {
        state.subdomain = p || null;
        try {
          if (typeof window !== 'undefined') {
            if (p) window.localStorage.setItem('subdomain', p);
            else window.localStorage.removeItem('subdomain');
          }
        } catch {}
        return;
      }
      if ('id' in p) {
        state.id = p.id ?? null;
        try {
          if (typeof window !== 'undefined') {
            if (p.id) window.localStorage.setItem('tenantId', p.id);
            else window.localStorage.removeItem('tenantId');
          }
        } catch {}
      }
      if ('subdomain' in p) {
        state.subdomain = p.subdomain ?? null;
        try {
          if (typeof window !== 'undefined') {
            if (p.subdomain) window.localStorage.setItem('subdomain', p.subdomain);
            else window.localStorage.removeItem('subdomain');
          }
        } catch {}
      }
      if ('name' in p) state.name = p.name ?? null;
      if ('logoUrl' in p) state.logoUrl = p.logoUrl ?? null;
    },
    setSubdomain(state, action: PayloadAction<string | null>) {
      state.subdomain = action.payload ?? null;
      try {
        if (typeof window !== 'undefined') {
          if (action.payload) window.localStorage.setItem('subdomain', action.payload);
          else window.localStorage.removeItem('subdomain');
        }
      } catch {}
    },
    setTenantId(state, action: PayloadAction<string | null>) {
      state.id = action.payload ?? null;
      try {
        if (typeof window !== 'undefined') {
          if (action.payload) window.localStorage.setItem('tenantId', action.payload);
          else window.localStorage.removeItem('tenantId');
        }
      } catch {}
    },
    clearTenant(state) {
      state.id = null;
      state.subdomain = null;
      state.name = null;
      state.logoUrl = null;
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('tenantId');
          window.localStorage.removeItem('subdomain');
        }
      } catch {}
    },
  },
});

export const { setTenant, setSubdomain, setTenantId, clearTenant } = tenantSlice.actions;
export default tenantSlice.reducer;
